import { parseRoles } from "./roles";
import { expectArray, expectBoolean, expectNumber, expectOptionalString, expectRecord, expectString } from "./schema";
export const MEMBER_COMMUNICATION_CATEGORIES = ["general", "lessonSupport", "replacement", "eventSupport"];
export const MEMBER_COMMUNICATION_TARGET_TYPES = ["tenant", "lesson", "occurrence", "event"];
export const MEMBER_COMMUNICATION_PUBLISH_STATUSES = ["draft", "scheduled", "published", "hidden", "expired", "deleted"];
export const MEMBER_COMMUNICATION_ATTACHMENT_TYPES = ["image", "video", "pdf"];
export const MEMBER_COMMUNICATION_ATTACHMENT_UPLOAD_STATUSES = ["draft", "uploadIssued", "uploaded", "failed"];
export const MEMBER_COMMUNICATION_ATTACHMENT_PROCESSING_STATUSES = ["notRequired", "pending", "processing", "ready", "failed"];
function parseStringEnum(value, allowedValues, path) {
    const parsed = expectString(value, path);
    if (!allowedValues.includes(parsed)) {
        throw new Error(`${path} must be one of: ${allowedValues.join(", ")}`);
    }
    return parsed;
}
function hasOwn(record, key) {
    return Object.prototype.hasOwnProperty.call(record, key);
}
function trimOptionalMax(value, path, maxLength) {
    const parsed = expectOptionalString(value, path);
    if (parsed && parsed.length > maxLength) {
        throw new Error(`${path} must be at most ${maxLength} characters`);
    }
    return parsed;
}
function expectOptionalNumber(value, path) {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value !== "number" || !Number.isFinite(value)) {
        throw new Error(`${path} must be a finite number`);
    }
    return value;
}
function expectOptionalNonNegativeNumber(value, path) {
    const parsed = expectOptionalNumber(value, path);
    if (parsed !== null && parsed < 0) {
        throw new Error(`${path} must be non-negative`);
    }
    return parsed;
}
function validateTarget(targetType, targetId, path) {
    if (targetType === "tenant" && targetId !== null) {
        throw new Error(`${path}.targetId must be null for tenant target`);
    }
    if (targetType !== "tenant" && !targetId) {
        throw new Error(`${path}.targetId is required for ${targetType} target`);
    }
}
function validatePublishWindow(publishFrom, publishTo, path) {
    if (!publishFrom || !publishTo) {
        return;
    }
    const from = Date.parse(publishFrom);
    const to = Date.parse(publishTo);
    if (!Number.isFinite(from) || !Number.isFinite(to) || from >= to) {
        throw new Error(`${path}.publishFrom must precede ${path}.publishTo`);
    }
}
function normalizeAttachmentType(value, path = "attachmentType") {
    return parseStringEnum(value, MEMBER_COMMUNICATION_ATTACHMENT_TYPES, path);
}
export function parseMemberCommunicationCategory(value, path = "category") {
    return parseStringEnum(value, MEMBER_COMMUNICATION_CATEGORIES, path);
}
export function parseMemberCommunicationTargetType(value, path = "targetType") {
    return parseStringEnum(value, MEMBER_COMMUNICATION_TARGET_TYPES, path);
}
export function parseMemberCommunicationPublishStatus(value, path = "publishStatus") {
    return parseStringEnum(value, MEMBER_COMMUNICATION_PUBLISH_STATUSES, path);
}
export function parseMemberCommunicationAttachmentUploadStatus(value, path = "uploadStatus") {
    return parseStringEnum(value, MEMBER_COMMUNICATION_ATTACHMENT_UPLOAD_STATUSES, path);
}
export function parseMemberCommunicationAttachmentProcessingStatus(value, path = "processingStatus") {
    return parseStringEnum(value, MEMBER_COMMUNICATION_ATTACHMENT_PROCESSING_STATUSES, path);
}
export function parseMemberCommunicationAttachment(value, path = "memberCommunicationAttachment") {
    const record = expectRecord(value, path);
    const sortOrder = expectNumber(record.sortOrder, `${path}.sortOrder`);
    if (sortOrder < 0) {
        throw new Error(`${path}.sortOrder must be non-negative`);
    }
    return {
        attachmentId: expectString(record.attachmentId, `${path}.attachmentId`),
        attachmentType: normalizeAttachmentType(record.attachmentType, `${path}.attachmentType`),
        sortOrder,
        originalFileName: expectOptionalString(record.originalFileName, `${path}.originalFileName`),
        mimeType: expectOptionalString(record.mimeType, `${path}.mimeType`),
        fileSizeBytes: expectOptionalNonNegativeNumber(record.fileSizeBytes, `${path}.fileSizeBytes`),
        width: expectOptionalNonNegativeNumber(record.width, `${path}.width`),
        height: expectOptionalNonNegativeNumber(record.height, `${path}.height`),
        durationSeconds: expectOptionalNonNegativeNumber(record.durationSeconds, `${path}.durationSeconds`),
        uploadStatus: parseMemberCommunicationAttachmentUploadStatus(record.uploadStatus, `${path}.uploadStatus`),
        processingStatus: parseMemberCommunicationAttachmentProcessingStatus(record.processingStatus, `${path}.processingStatus`),
        thumbnailAvailable: expectBoolean(record.thumbnailAvailable, `${path}.thumbnailAvailable`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseMemberCommunication(value, path = "memberCommunication") {
    const record = expectRecord(value, path);
    const targetType = parseMemberCommunicationTargetType(record.targetType, `${path}.targetType`);
    const targetId = expectOptionalString(record.targetId, `${path}.targetId`);
    const publishFrom = expectOptionalString(record.publishFrom, `${path}.publishFrom`);
    const publishTo = expectOptionalString(record.publishTo, `${path}.publishTo`);
    const attachments = expectArray(record.attachments, parseMemberCommunicationAttachment, `${path}.attachments`);
    const attachmentIds = new Set();
    for (const attachment of attachments) {
        if (attachmentIds.has(attachment.attachmentId)) {
            throw new Error(`${path}.attachments contains duplicate attachmentId`);
        }
        attachmentIds.add(attachment.attachmentId);
    }
    validateTarget(targetType, targetId, path);
    validatePublishWindow(publishFrom, publishTo, path);
    return {
        communicationId: expectString(record.communicationId, `${path}.communicationId`),
        title: trimOptionalMax(record.title, `${path}.title`, 120),
        body: trimOptionalMax(record.body, `${path}.body`, 5000),
        category: parseMemberCommunicationCategory(record.category, `${path}.category`),
        targetType,
        targetId,
        targetLabel: expectString(record.targetLabel, `${path}.targetLabel`),
        publishStatus: parseMemberCommunicationPublishStatus(record.publishStatus, `${path}.publishStatus`),
        publishFrom,
        publishTo,
        visibleToRoles: parseRoles(record.visibleToRoles, `${path}.visibleToRoles`),
        allowTrialViewer: expectBoolean(record.allowTrialViewer, `${path}.allowTrialViewer`),
        requireActiveEnrollment: expectBoolean(record.requireActiveEnrollment, `${path}.requireActiveEnrollment`),
        attachments,
        createdByPlatformUserId: expectString(record.createdByPlatformUserId, `${path}.createdByPlatformUserId`),
        createdByRoleSnapshot: expectString(record.createdByRoleSnapshot, `${path}.createdByRoleSnapshot`),
        publishedAt: expectOptionalString(record.publishedAt, `${path}.publishedAt`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseViewerMemberCommunication(value, path = "viewerMemberCommunication") {
    const record = expectRecord(value, path);
    return {
        ...parseMemberCommunication(record, path),
        readAt: expectOptionalString(record.readAt, `${path}.readAt`),
        isUnread: expectBoolean(record.isUnread, `${path}.isUnread`)
    };
}
export function parseViewerMemberCommunicationsResponse(value, path = "viewerMemberCommunicationsResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseViewerMemberCommunication, `${path}.items`),
        unreadCount: expectNumber(record.unreadCount, `${path}.unreadCount`)
    };
}
export function parseCreateMemberCommunicationRequest(value, path = "createMemberCommunicationRequest") {
    const record = expectRecord(value, path);
    const title = trimOptionalMax(record.title, `${path}.title`, 120);
    const body = trimOptionalMax(record.body, `${path}.body`, 5000);
    const targetType = parseMemberCommunicationTargetType(record.targetType, `${path}.targetType`);
    const targetId = expectOptionalString(record.targetId, `${path}.targetId`);
    const publishFrom = expectOptionalString(record.publishFrom, `${path}.publishFrom`);
    const publishTo = expectOptionalString(record.publishTo, `${path}.publishTo`);
    const attachmentType = record.attachmentType === null || record.attachmentType === undefined
        ? null
        : normalizeAttachmentType(record.attachmentType, `${path}.attachmentType`);
    validateTarget(targetType, targetId, path);
    validatePublishWindow(publishFrom, publishTo, path);
    if (!title && !body && !attachmentType) {
        throw new Error("COMMUNICATION_CONTENT_REQUIRED");
    }
    return {
        title,
        body,
        category: parseMemberCommunicationCategory(record.category, `${path}.category`),
        targetType,
        targetId,
        publishFrom,
        publishTo,
        attachmentType
    };
}
export function parseUpdateMemberCommunicationRequest(value, path = "updateMemberCommunicationRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "title")) {
        parsed.title = trimOptionalMax(record.title, `${path}.title`, 120);
    }
    if (hasOwn(record, "body")) {
        parsed.body = trimOptionalMax(record.body, `${path}.body`, 5000);
    }
    if (hasOwn(record, "publishFrom")) {
        parsed.publishFrom = expectOptionalString(record.publishFrom, `${path}.publishFrom`);
    }
    if (hasOwn(record, "publishTo")) {
        parsed.publishTo = expectOptionalString(record.publishTo, `${path}.publishTo`);
    }
    validatePublishWindow(parsed.publishFrom ?? null, parsed.publishTo ?? null, path);
    return parsed;
}
export function parseCreateMemberCommunicationAttachmentRequest(value, path = "createMemberCommunicationAttachmentRequest") {
    const record = expectRecord(value, path);
    const fileSizeBytes = expectNumber(record.fileSizeBytes, `${path}.fileSizeBytes`);
    const sortOrder = expectNumber(record.sortOrder, `${path}.sortOrder`);
    if (fileSizeBytes <= 0) {
        throw new Error(`${path}.fileSizeBytes must be positive`);
    }
    if (sortOrder < 0) {
        throw new Error(`${path}.sortOrder must be non-negative`);
    }
    return {
        attachmentType: normalizeAttachmentType(record.attachmentType, `${path}.attachmentType`),
        originalFileName: expectString(record.originalFileName, `${path}.originalFileName`).trim(),
        mimeType: expectString(record.mimeType, `${path}.mimeType`).trim(),
        fileSizeBytes,
        sortOrder
    };
}
export function parseCompleteMemberCommunicationAttachmentUploadRequest(value, path = "completeMemberCommunicationAttachmentUploadRequest") {
    const record = expectRecord(value, path);
    return {
        uploadSessionId: expectString(record.uploadSessionId, `${path}.uploadSessionId`),
        uploadSessionToken: expectString(record.uploadSessionToken, `${path}.uploadSessionToken`),
        durationSeconds: expectOptionalNonNegativeNumber(record.durationSeconds, `${path}.durationSeconds`),
        width: expectOptionalNonNegativeNumber(record.width, `${path}.width`),
        height: expectOptionalNonNegativeNumber(record.height, `${path}.height`)
    };
}
export function parseMemberCommunicationAttachmentUploadSessionResponse(value, path = "memberCommunicationAttachmentUploadSessionResponse") {
    const record = expectRecord(value, path);
    const headers = expectRecord(record.requiredHeaders, `${path}.requiredHeaders`);
    return {
        communicationId: expectString(record.communicationId, `${path}.communicationId`),
        attachmentId: expectString(record.attachmentId, `${path}.attachmentId`),
        uploadSessionId: expectString(record.uploadSessionId, `${path}.uploadSessionId`),
        uploadUrl: expectString(record.uploadUrl, `${path}.uploadUrl`),
        uploadSessionToken: expectString(record.uploadSessionToken, `${path}.uploadSessionToken`),
        expiresAt: expectString(record.expiresAt, `${path}.expiresAt`),
        requiredHeaders: Object.fromEntries(Object.entries(headers).filter((entry) => typeof entry[1] === "string"))
    };
}
export function parseMemberCommunicationAttachmentAccessUrlResponse(value, path = "memberCommunicationAttachmentAccessUrlResponse") {
    const record = expectRecord(value, path);
    return {
        communicationId: expectString(record.communicationId, `${path}.communicationId`),
        attachmentId: expectString(record.attachmentId, `${path}.attachmentId`),
        attachmentType: normalizeAttachmentType(record.attachmentType, `${path}.attachmentType`),
        accessUrl: expectString(record.accessUrl, `${path}.accessUrl`),
        expiresAt: expectString(record.expiresAt, `${path}.expiresAt`)
    };
}
export function parseMemberCommunicationThumbnailAccessUrlResponse(value, path = "memberCommunicationThumbnailAccessUrlResponse") {
    const record = expectRecord(value, path);
    return {
        communicationId: expectString(record.communicationId, `${path}.communicationId`),
        attachmentId: expectString(record.attachmentId, `${path}.attachmentId`),
        accessUrl: expectString(record.accessUrl, `${path}.accessUrl`),
        expiresAt: expectString(record.expiresAt, `${path}.expiresAt`)
    };
}
export function parseMemberCommunicationTargetOption(value, path = "memberCommunicationTargetOption") {
    const record = expectRecord(value, path);
    const targetType = parseMemberCommunicationTargetType(record.targetType, `${path}.targetType`);
    if (targetType === "tenant") {
        throw new Error(`${path}.targetType must not be tenant`);
    }
    return {
        targetType,
        targetId: expectString(record.targetId, `${path}.targetId`),
        label: expectString(record.label, `${path}.label`),
        subtitle: expectOptionalString(record.subtitle, `${path}.subtitle`),
        status: expectOptionalString(record.status, `${path}.status`),
        scheduledAt: expectOptionalString(record.scheduledAt, `${path}.scheduledAt`)
    };
}
export function parseMemberCommunicationTargetOptionsResponse(value, path = "memberCommunicationTargetOptionsResponse") {
    const record = expectRecord(value, path);
    const targetType = parseMemberCommunicationTargetType(record.targetType, `${path}.targetType`);
    if (targetType === "tenant") {
        throw new Error(`${path}.targetType must not be tenant`);
    }
    return {
        targetType,
        items: expectArray(record.items, parseMemberCommunicationTargetOption, `${path}.items`)
    };
}
